/**
 * SupportWidget — Botón flotante único de ayuda (bottom-right). En reposo
 * (idle) ofrece un menú con 2 opciones: abrir el chat en vivo (delega al
 * panel del chat-widget vía window.EcoContChat.open(), que se monta con
 * hideLauncher para no mostrar un segundo botón) o pedir soporte remoto
 * (co-browsing con un agente del CRM, este mismo hook).
 *
 * Se monta una sola vez en AppLayout y permanece visible en todas las
 * páginas mientras el usuario está autenticado.
 *
 * Estados (post-selección de "soporte remoto"):
 *   idle      → botón único con menú "Chat en vivo" / "Soporte remoto"
 *   connecting → breve transición (< 1 s normalmente)
 *   waiting   → "Buscando agente…" + spinner + botón cancelar
 *   active    → "En soporte" + indicador verde pulsante + botón finalizar
 *   ended     → "Sesión finalizada" (3 s, luego vuelve a idle)
 *   error     → "Sin conexión" (3 s, luego vuelve a idle)
 */
import { AlertTriangle, Headphones, LifeBuoy, Loader2, MessageCircle, Mic, MicOff, Minus, MonitorUp, MousePointerClick, PhoneOff, Send, Video, WifiOff, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';


import { useCobrowseSupport } from '@/lib/cobrowse-support';

import SupportReasonEditor from './SupportReasonEditor';

const CRM_API_BASE_URL = import.meta.env.VITE_CRM_API_BASE_URL as string | undefined;

export function SupportWidget() {
  const {
    status, requestSupport, endSession,
    remoteControlEnabled, toggleRemoteControl,
    muted, toggleMute, remoteStream, enableVideo,
    screenSharing, screenShareBlocked, shareScreen,
    agentDisconnected,
    micPermissionHint, micBlocked, cameraBlocked,
  } = useCobrowseSupport();

  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const [remoteHasVideo, setRemoteHasVideo] = useState(false);

  // Menú del botón unificado (reposo): elegir entre chat en vivo (delegado
  // al panel del chat-widget, ver window.EcoContChat) o soporte remoto
  // (cobrowsing, este mismo hook) — reemplaza los 2 botones separados que
  // confundían al cliente.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [menuOpen]);

  function openChat(): void {
    setMenuOpen(false);
    (window as unknown as { EcoContChat?: { open?: () => void } }).EcoContChat?.open?.();
  }

  // Horario de atención — el soporte remoto (co-browsing) requiere un
  // agente en vivo, así que NO puede pedirse fuera de horario (a diferencia
  // del chat, que sigue recibiendo mensajes siempre). Se consulta una sola
  // vez al montar; si falla o no está configurado VITE_CRM_API_BASE_URL,
  // `businessHours` queda null y el botón se comporta como siempre
  // disponible (fail-open, nunca bloquea por un error de red/config).
  const [businessHours, setBusinessHours] = useState<{ open: boolean; schedule: string } | null>(null);
  const supportBlocked = businessHours !== null && !businessHours.open;

  useEffect(() => {
    if (!CRM_API_BASE_URL) return;
    fetch(`${CRM_API_BASE_URL}/external/business-hours`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setBusinessHours({ open: !!data.open, schedule: data.schedule ?? '' });
      })
      .catch(() => { /* best-effort — sin bloqueo si falla */ });
  }, []);

  // Antes de conectar, se le pide al usuario un detalle de su caso — así el
  // agente sabe de qué se trata (o a quién asignarlo) sin tener que
  // preguntar primero. `describing` abre ese pasito intermedio; el texto se
  // manda recién al confirmar (ver handleSubmitReason).
  const [describing, setDescribing] = useState(false);
  const [reasonHtml, setReasonHtml] = useState('');

  function handleOpenReasonForm(): void {
    if (supportBlocked) return;
    setMenuOpen(false);
    setReasonHtml('');
    setDescribing(true);
  }

  function handleSubmitReason(): void {
    if (!reasonHtml) return;
    setDescribing(false);
    requestSupport(reasonHtml);
  }

  // Panel de sesión activa: movible (arrastre libre) y minimizable a una
  // píldora — el cliente puede necesitar ver contenido que el panel tapa
  // mientras el agente lo guía. `position` null = posición por defecto
  // (esquina, vía clases Tailwind); no-null = coordenadas absolutas tras
  // arrastrar. Ambos se resetean al arrancar una sesión nueva.
  const [minimized, setMinimized] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });
  const dragMovedRef = useRef(false);

  useEffect(() => {
    if (status === 'connecting') {
      setPosition(null);
      setMinimized(false);
    }
  }, [status]);

  function handleDragPointerDown(e: React.PointerEvent<HTMLElement>): void {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    dragMovedRef.current = false;
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handleDragPointerMove(e: React.PointerEvent<HTMLElement>): void {
    if (!draggingRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    // Umbral chico para distinguir "arrastré" de "hice click" — clave para
    // que la píldora minimizada no se reabra sola justo al soltarla.
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragMovedRef.current = true;

    const margin = 8;
    const width = panelRef.current?.offsetWidth ?? 288;
    const height = panelRef.current?.offsetHeight ?? 160;
    const x = Math.min(Math.max(e.clientX - dragOffsetRef.current.x, margin), window.innerWidth - width - margin);
    const y = Math.min(Math.max(e.clientY - dragOffsetRef.current.y, margin), window.innerHeight - height - margin);
    setPosition({ x, y });
  }

  function handleDragPointerUp(e: React.PointerEvent<HTMLElement>): void {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }

  function handleTogglePill(): void {
    if (dragMovedRef.current) {
      dragMovedRef.current = false;
      return;
    }
    setMinimized(false);
  }

  // <audio>/<video> están SIEMPRE montados (ver JSX abajo) — si el <video>
  // se montara solo condicionalmente a partir de este mismo efecto, el ref
  // seguiría siendo null en esta pasada y la asignación de srcObject se
  // perdería en silencio, dejando el cuadro en negro.
  useEffect(() => {
    if (!remoteStream) {
      setRemoteHasVideo(false);
      return;
    }
    const hasVideo = remoteStream.getVideoTracks().length > 0;
    setRemoteHasVideo(hasVideo);
    // Solo uno de los dos reproduce a la vez — evita eco/doble audio.
    if (hasVideo) {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream;
        remoteVideoRef.current.play().catch(() => { /* autoplay bloqueado */ });
      }
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    } else {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remoteStream;
        remoteAudioRef.current.play().catch(() => { /* idem */ });
      }
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    }
  }, [remoteStream]);

  if (status === 'idle' && describing) {
    return (
      <div className="fixed bottom-20 right-6 lg:bottom-6 z-900 flex w-72 flex-col gap-2.5 rounded-2xl border border-border bg-background p-3 shadow-xl">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Headphones className="h-4 w-4 text-primary" />
            Soporte remoto
          </span>
          <button
            onClick={() => { setDescribing(false); }}
            title="Cancelar"
            className="flex h-6 w-6 items-center justify-center rounded-full border-0 bg-muted text-muted-foreground hover:bg-muted/70 transition-colors outline-hidden"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Contanos en qué necesitás ayuda — podés pegar una captura de pantalla con Ctrl+V para mostrar el problema.
        </p>
        <SupportReasonEditor
          value={reasonHtml}
          onChange={setReasonHtml}
          placeholder="Ej: no me deja generar el comprobante de la venta #1234…"
        />
        <button
          onClick={handleSubmitReason}
          disabled={!reasonHtml}
          className="flex items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
          Solicitar soporte
        </button>
      </div>
    );
  }

  if (status === 'idle') {
    return (
      <div ref={menuRef} className="fixed bottom-20 right-6 lg:bottom-6 z-900 flex flex-col items-end gap-2">
        {menuOpen && (
          <div className="flex w-56 flex-col gap-1 rounded-2xl border border-border bg-background p-2 shadow-xl">
            <button
              onClick={openChat}
              className="flex items-center gap-2.5 rounded-xl border-0 bg-transparent px-3 py-2.5 text-left text-sm font-medium text-foreground outline-hidden transition-colors hover:bg-muted"
            >
              <MessageCircle className="h-4 w-4 shrink-0 text-primary" />
              Chat en vivo
            </button>
            <button
              onClick={handleOpenReasonForm}
              disabled={supportBlocked}
              className={`flex items-center gap-2.5 rounded-xl border-0 bg-transparent px-3 py-2.5 text-left text-sm font-medium outline-hidden transition-colors ${
                supportBlocked ? 'cursor-not-allowed text-muted-foreground/60' : 'text-foreground hover:bg-muted'
              }`}
            >
              <Headphones className={`h-4 w-4 shrink-0 ${supportBlocked ? 'text-muted-foreground/60' : 'text-primary'}`} />
              <span className="flex flex-col">
                <span>Soporte remoto</span>
                {supportBlocked && (
                  <span className="text-[10px] font-normal leading-tight text-muted-foreground">
                    Fuera de horario{businessHours?.schedule ? ` · ${businessHours.schedule}` : ''}
                  </span>
                )}
              </span>
            </button>
          </div>
        )}
        <button
          onClick={() => { setMenuOpen((v) => !v); }}
          title="Ayuda y soporte"
          aria-label="Ayuda y soporte"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all hover:shadow-xl hover:brightness-110 border-0 outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <LifeBuoy className="h-6 w-6" />}
        </button>
      </div>
    );
  }

  if (status === 'connecting') {
    return (
      <div className="fixed bottom-20 right-6 lg:bottom-6 z-900 flex items-center gap-2 rounded-full bg-primary/80 px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-lg">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span>Conectando…</span>
      </div>
    );
  }

  if (status === 'waiting') {
    return (
      <div className="fixed bottom-20 right-6 lg:bottom-6 z-900 flex flex-col items-end gap-2">
        <div className="flex items-center gap-3 rounded-2xl bg-background px-4 py-3 shadow-xl border border-orange-200 dark:border-orange-800">
          <Loader2 className="h-4 w-4 animate-spin text-orange-500 shrink-0" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-foreground">Buscando agente…</span>
            <span className="text-xs text-muted-foreground">Tu solicitud fue enviada</span>
          </div>
          <button
            onClick={openChat}
            title="Chat en vivo"
            className="ml-2 flex h-6 w-6 items-center justify-center rounded-full border-0 bg-muted text-muted-foreground hover:bg-muted/70 transition-colors outline-hidden"
          >
            <MessageCircle className="h-3 w-3" />
          </button>
          <button
            onClick={endSession}
            title="Cancelar"
            className="flex h-6 w-6 items-center justify-center rounded-full border-0 bg-muted text-muted-foreground hover:bg-destructive hover:text-white transition-colors outline-hidden"
          >
            <X className="h-3 w-3" />
          </button>
        </div>

        {/* Guía preventiva — solo si el permiso de mic todavía no está
            concedido (nunca se muestra si ya lo está, para no molestar). */}
        {micPermissionHint && micPermissionHint !== 'granted' && (
          <div className="flex max-w-[260px] items-start gap-1.5 rounded-xl bg-background px-3 py-2 text-[11px] text-muted-foreground shadow-lg border border-border">
            <Mic className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary" />
            Cuando conectes, tu navegador te va a pedir permiso de micrófono para que puedas hablar con el agente — hacé clic en &quot;Permitir&quot;.
          </div>
        )}
      </div>
    );
  }

  if (status === 'active') {
    const panelStyle: React.CSSProperties | undefined = position
      ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
      : undefined;
    const posClass = position ? '' : 'bottom-20 right-6 lg:bottom-6';

    if (minimized) {
      return (
        <div ref={panelRef} style={panelStyle} className={`fixed z-900 ${posClass}`}>
          {/* Audio/video remotos siguen montados minimizado — la llamada no se corta. */}
          <audio ref={remoteAudioRef} autoPlay className="hidden" />
          <video ref={remoteVideoRef} autoPlay playsInline className="hidden" />
          <button
            onPointerDown={handleDragPointerDown}
            onPointerMove={handleDragPointerMove}
            onPointerUp={handleDragPointerUp}
            onClick={handleTogglePill}
            title="Mostrar panel de soporte"
            className="flex touch-none select-none items-center gap-2 rounded-full bg-background px-3 py-2 shadow-xl border border-emerald-200 dark:border-emerald-800 outline-hidden cursor-move"
          >
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
            </span>
            <span className="text-xs font-medium text-foreground">Soporte</span>
          </button>
        </div>
      );
    }

    return (
      <div ref={panelRef} style={panelStyle} className={`fixed z-900 flex flex-col items-end gap-2 ${posClass}`}>
        {/* Audio y video remotos SIEMPRE montados (nunca condicionalmente) —
            así el ref existe desde el primer render. Se ocultan por CSS. */}
        <audio ref={remoteAudioRef} autoPlay className="hidden" />
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className={`h-24 w-36 rounded-lg border border-white/20 bg-black object-cover shadow-xl ${remoteHasVideo ? '' : 'hidden'}`}
        />

        <div className="flex w-72 flex-col gap-2.5 rounded-2xl bg-background px-4 py-3 shadow-xl border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center gap-2">
            <div
              onPointerDown={handleDragPointerDown}
              onPointerMove={handleDragPointerMove}
              onPointerUp={handleDragPointerUp}
              title="Arrastrar para mover"
              className="flex flex-1 touch-none select-none items-center gap-3 cursor-move"
            >
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
              </span>
              <div className="flex flex-1 flex-col">
                <span className="text-sm font-semibold text-foreground">En sesión de soporte</span>
                {agentDisconnected ? (
                  <span className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400">
                    <WifiOff className="h-3 w-3" />
                    Agente desconectado, esperando…
                  </span>
                ) : (
                  <span className="text-xs text-emerald-600 dark:text-emerald-400">Agente conectado</span>
                )}
              </div>
            </div>
            <button
              onClick={openChat}
              title="Chat en vivo"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-0 bg-muted text-muted-foreground hover:bg-muted/70 transition-colors outline-hidden"
            >
              <MessageCircle className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => { setMinimized(true); }}
              title="Minimizar"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-0 bg-muted text-muted-foreground hover:bg-muted/70 transition-colors outline-hidden"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={endSession}
              title="Finalizar sesión"
              className="flex shrink-0 items-center gap-1.5 rounded-full border-0 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-100 transition-colors outline-hidden dark:bg-red-950/40 dark:text-red-400"
            >
              <PhoneOff className="h-3 w-3" />
            </button>
          </div>

          {/* Compartir pantalla — acción PRINCIPAL de esta sesión (sin esto
              el agente no ve nada). Requiere gesto del usuario, no se puede
              automatizar. Botón prominente en vez de un ícono chico más,
              porque el soporte depende de esto. */}
          {screenSharing ? (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-2.5 py-2 text-xs font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              Compartiendo tu pantalla
            </div>
          ) : (
            <button
              onClick={shareScreen}
              className="flex items-center justify-center gap-1.5 rounded-lg border-0 bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:brightness-110 outline-hidden"
            >
              <MonitorUp className="h-3.5 w-3.5" />
              Compartir mi pantalla
            </button>
          )}
          {screenShareBlocked && (
            <div className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Cancelaste el diálogo de compartir pantalla, o tu sistema operativo lo bloqueó. Volvé a tocar &quot;Compartir mi pantalla&quot; e
                importante: elegí <strong>&quot;Toda la pantalla&quot;</strong> (no &quot;Pestaña&quot;) en el diálogo del navegador — así el agente
                puede ver también los desplegables/menús que se abren.
              </span>
            </div>
          )}

          {/* Mic / cámara */}
          <div className="flex items-center gap-1.5 border-t border-border pt-2">
            <button
              onClick={toggleMute}
              title={muted ? 'Activar micrófono' : 'Silenciar micrófono'}
              className="flex h-7 w-7 items-center justify-center rounded-full border-0 bg-muted text-muted-foreground hover:bg-muted/70 transition-colors outline-hidden"
            >
              {muted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            </button>
            <button
              onClick={enableVideo}
              title="Compartir mi cámara"
              className="flex h-7 w-7 items-center justify-center rounded-full border-0 bg-muted text-muted-foreground hover:bg-muted/70 transition-colors outline-hidden"
            >
              <Video className="h-3.5 w-3.5" />
            </button>
            <span className="text-xs text-muted-foreground">Llamada de soporte</span>
          </div>

          {/* Aviso: permiso de mic/cámara realmente denegado — con
              instrucciones de cómo habilitarlo a mano. El mismo botón de
              arriba (toggleMute/enableVideo) sirve de "Reintentar". */}
          {(micBlocked || cameraBlocked) && (
            <div className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Bloqueaste el acceso a {micBlocked && cameraBlocked ? 'micrófono y cámara' : micBlocked ? 'tu micrófono' : 'tu cámara'} para este sitio.
                Hacé clic en el ícono de candado/información junto a la dirección del navegador, habilitá el permiso y volvé a tocar el botón de{' '}
                {micBlocked && cameraBlocked ? 'micrófono o cámara' : micBlocked ? 'micrófono' : 'cámara'} para reintentar.
              </span>
            </div>
          )}

          {/* Switch: permitir control remoto */}
          <button
            onClick={toggleRemoteControl}
            className="flex items-center justify-between gap-2 rounded-lg border-0 bg-muted px-2.5 py-1.5 text-left outline-hidden transition-colors hover:bg-muted/70"
          >
            <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
              <MousePointerClick className="h-3.5 w-3.5" />
              Permitir control remoto
            </span>
            <span
              className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
                remoteControlEnabled ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-700'
              }`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                  remoteControlEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                }`}
              />
            </span>
          </button>
        </div>
      </div>
    );
  }

  if (status === 'ended') {
    return (
      <div className="fixed bottom-20 right-6 lg:bottom-6 z-900 flex items-center gap-2 rounded-full bg-muted px-4 py-2.5 text-sm text-muted-foreground shadow-md">
        <Headphones className="h-4 w-4 shrink-0" />
        <span>Sesión finalizada</span>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="fixed bottom-20 right-6 lg:bottom-6 z-900 flex items-center gap-2 rounded-full bg-red-50 px-4 py-2.5 text-sm text-red-600 shadow-md border border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800">
        <PhoneOff className="h-4 w-4 shrink-0" />
        <span>Sin conexión de soporte</span>
      </div>
    );
  }

  return null;
}
